import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import PhotoViewer from '../ui/PhotoViewer';
import { supabase } from '../../lib/supabase';
import { getServices, upsertService, deleteServiceApi, upsertMachine, logTrashItem, restoreTrashItem } from '../../lib/db';
import { ACC, MUT, BRD, BRD2, SURF, TXT, RED, GRN, btnA, btnG, btnD, dvdr, sm } from '../../lib/styles';
import { MACHINE_TYPES, DEFAULT_TILE, ALL_BADGE_FIELDS, BADGE_PALETTE, TILE_COLOR_DEFAULTS } from '../../lib/constants';
import { SL, FL, Empty, SkullRating, SpecCell, TileConfig, ExpandConfig } from '../ui/shared';
import { mIcon, fmtDT, getMachineServiceStatus, findMachineSpecMatch } from '../../lib/helpers';
import { getMachineSpecEntries, humanizeKey, DEFAULT_EXPAND } from '../../lib/machineSpecs';
import { hl } from '../wiki/wikiSearchHighlight';
import { WikiTrackerModal } from '../wiki/WikiModals';
import { deletePhoto } from '../../lib/storage';
import { toastError, toastUndo } from '../../lib/toast';
import { useFeatureFlags } from '../../lib/featureFlagsContext';
const PdfExportModal = lazy(() => import('../pdf/PdfExportModal'));
import ServiceModal from '../ui/ServiceModal';
import MachineForm from './MachineForm';
import MoveToStoragePanel from './MoveToStoragePanel';
function MachineCard({machine,onUpdate,onDelete,company,profile,clients,isGuest,showGuide,onTutDismiss,onCardOpened,initialOpen,hideCollapse,onClose,searchQuery,searchTokens,onMoveToBench,onBookIn}){
  const {wiki:wikiFlagOn}=useFeatureFlags();
  const [open,setOpen]=useState(!!initialOpen);
  const [svcs,setSvcs]=useState([]);
  const [loaded,setLoaded]=useState(false);
  const [showEdit,setShowEdit]=useState(false);
  const [showSvc,setShowSvc]=useState(false);
  const [editSvc,setEditSvc]=useState(null);
  const [saving,setSaving]=useState(false);
  const [fullImg,setFullImg]=useState(null);
  const [showConfig,setShowConfig]=useState(false);
  const [showWiki,setShowWiki]=useState(false);
  const [showExpandConfig,setShowExpandConfig]=useState(false);
  const [showPdfOpts,setShowPdfOpts]=useState(false);
  const [copied,setCopied]=useState(false);
  const m=machine;
  // The plain-text type caption below and the "Type:" tile badge both show
  // m.type — only show the caption when the badge isn't already covering it,
  // so type doesn't render twice (badge is on by default via DEFAULT_TILE).
  const typeBadgeActive=(m.tileFields&&m.tileFields.length>0?m.tileFields:DEFAULT_TILE).includes("type");
  // Notify parent when card opens so the above-card guide arrow can hide
  useEffect(()=>{if(open&&showGuide)onCardOpened?.();},[open,showGuide]);
  const withGuide=(desc,el)=>showGuide?(
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
      {el}
      <svg className="arrow-guide" width="18" height="14" viewBox="0 0 18 14" style={{display:"block"}}>
        <path d="M 9 12 C 12 7, 6 5, 9 2" stroke="#e8870a" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
        <path d="M 6 4 L 9 1 L 12 4" stroke="#e8870a" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span style={{fontSize:7,color:"#e8870a",fontFamily:"'IBM Plex Mono',monospace",textAlign:"center",lineHeight:"1.3",whiteSpace:"pre-line"}}>{desc}</span>
    </div>
  ):el;
  const openRef = useRef(false);
  openRef.current = open;

  const clientName = useMemo(() => {
    if (!m.clientId || !clients?.length) return null;
    return clients.find(c => c.id === m.clientId)?.name || null;
  }, [m.clientId, clients]);

  // Refetch on every open (and machine change) — a cached-once list goes
  // stale when entries are added elsewhere (PlugLog, another device).
  useEffect(()=>{
    if(!open) return;
    let alive=true;
    getServices(m.id).then(s=>{if(alive){setSvcs(s||[]);setLoaded(true);}});
    return ()=>{alive=false;};
  },[open,m.id]);

  const storagePolicyEnabled = !!(profile?.storage_policy_enabled);

  // Android back button collapses this card when it's open.
  // Pushing { cardOpen: id } means: back closes photo first (if open), then collapses card.
  // Skipped entirely when hideCollapse — that mode is the Tracker's full-screen
  // tile overlay, which owns back-button handling itself (closeTile) so one
  // press exits to the tracker instead of just collapsing the card underneath.
  useEffect(()=>{
    if(!open||hideCollapse) return;
    history.pushState({ cardOpen: m.id }, '');
    const onPop = e => {
      // If we landed on our own state somehow, ignore. Otherwise we were just popped.
      if (!openRef.current) return;
      if (e.state?.cardOpen === m.id) return;
      setOpen(false);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  },[open]);

  const saveSvc=async entry=>{
    setSaving(true);
    try{
      await upsertService(m.id,entry);
      setSvcs(prev=>prev.find(s=>s.id===entry.id)?prev.map(s=>s.id===entry.id?entry:s):[entry,...prev]);
      setShowSvc(false);setEditSvc(null);
    }finally{
      setSaving(false);
    }
  };
  const delSvc=async id=>{
    if(!confirm("Delete this entry?"))return;
    const svc=svcs.find(s=>s.id===id);
    // DB delete first — destroying photos before a failed delete leaves the
    // surviving record pointing at 404s.
    try{
      await deleteServiceApi(id);
    }catch(e){
      toastError("Delete failed — check connection");
      return;
    }
    if(svc?.plugPhoto) deletePhoto(svc.plugPhoto);
    (svc?.jobPhotos||[]).forEach(url=>deletePhoto(url));
    setSvcs(prev=>prev.filter(s=>s.id!==id));
  };

  // Goes to Recently Deleted for 30 days (supabase/trash_items.sql) instead of a
  // confirm() popup — same immediate-delete-with-Undo-toast pattern as the
  // whole-machine Delete button below.
  const deletePhotoAt=idx=>{
    const url=m.photos[idx];
    onUpdate({...m,photos:m.photos.filter((_,j)=>j!==idx)});
    logTrashItem({machineId:m.id,itemType:"photos",label:m.name?`Photo from ${m.name}`:"Photo",snapshot:url})
      .then(trashId=>{
        toastUndo("Photo deleted",async()=>{
          try{
            await restoreTrashItem(trashId);
            // Mirrors what restore_trash_item() actually does server-side —
            // appends to the end of the array, not back at its original index.
            onUpdate({...m,photos:[...m.photos.filter((_,j)=>j!==idx),url]});
          }catch(e){toastError("Couldn't undo — "+e.message);}
        });
      })
      .catch(e=>console.error("logTrashItem (photo):",e)); // best-effort — photo is already removed either way
  };

  const specs=getMachineSpecEntries(m);

  const timerRunning = m.jobTimers?.[0]?.status === "running";
  const svcStatus = getMachineServiceStatus(m);
  const specMatch = findMachineSpecMatch(m, searchQuery);

  const _jBase   = {cursor:"pointer",fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",padding:"9px 14px",borderRadius:2,fontFamily:"'IBM Plex Mono',monospace",border:"none",width:"100%",boxSizing:"border-box",minHeight:44,display:"flex",alignItems:"center",justifyContent:"center",textAlign:"center"};
  const _jAct    = {..._jBase,background:ACC,color:"#fff"};
  const _jEdit   = _jAct;
  const _jPdf    = _jAct;
  const _jWiki   = _jAct;
  const _jShare  = _jAct;
  const _jLayout = _jAct;
  const _jTile   = _jAct;
  const _jDel    = {..._jBase,background:RED,color:"#fff"};
  const _jClose  = {..._jBase,background:"transparent",border:"1px solid "+GRN+"55",color:GRN};

  return (
    <div className="panel-fastened" style={{background:SURF,border:"1px solid "+(timerRunning?GRN+"55":BRD),borderRadius:3,marginBottom:8,overflow:"hidden",position:"relative",boxShadow:timerRunning?"0 0 8px "+GRN+"22":undefined}}>
      <div className="screw tl" /><div className="screw tr" /><div className="screw bl" /><div className="screw br" />
      {fullImg&&(typeof fullImg==="object"
        ?<PhotoViewer src={fullImg.src} onClose={()=>setFullImg(null)} isCover={fullImg.idx===0} onSetCover={()=>{const r=[fullImg.src,...m.photos.filter((_,j)=>j!==fullImg.idx)];onUpdate({...m,photos:r});setFullImg({src:fullImg.src,idx:0});}} onDelete={()=>{deletePhotoAt(fullImg.idx);setFullImg(null);}} />
        :<PhotoViewer src={fullImg} onClose={()=>setFullImg(null)} />)}
      {showEdit&&<MachineForm existing={m} onSave={u=>{onUpdate(u);setShowEdit(false);}} onClose={()=>setShowEdit(false)} company={company} units={profile?.units||"metric"} profile={profile} isGuest={isGuest}/>}
      {showWiki&&<WikiTrackerModal machine={m} profile={profile} onClose={()=>setShowWiki(false)}/>}
      {showConfig&&<TileConfig machine={m} onSave={u=>{onUpdate(u);setShowConfig(false);}} onClose={()=>setShowConfig(false)} />
      }
      {showExpandConfig&&<ExpandConfig machine={m} onSave={u=>{onUpdate(u);setShowExpandConfig(false);}} onClose={()=>setShowExpandConfig(false)} />}
      {(showSvc||editSvc)&&<ServiceModal machine={m} existing={editSvc} onSave={saveSvc} onClose={()=>{setShowSvc(false);setEditSvc(null);}} />}

      {/* ── Collapsed card — poster style ── */}
      <div onClick={()=>{
        if(hideCollapse) return;
        // Closing via tap must pop the {cardOpen} history entry we pushed on
        // open (mirrors PhotoViewer) or stale entries pile up on the stack.
        if(open&&history.state?.cardOpen===m.id){setOpen(false);history.back();}
        else setOpen(o=>!o);
      }} style={{cursor:hideCollapse?"default":"pointer",userSelect:"none"}}>

        {/* Hero photo / icon placeholder */}
        {m.photos?.[0]
          ? <div style={{position:"relative"}}>
              <img src={m.photos[0]} alt="" style={{width:"100%",height:170,objectFit:"cover",display:"block"}} />
              <div style={{position:"absolute",bottom:0,left:0,right:0,height:"65%",background:"linear-gradient(to bottom, transparent, #161616)",pointerEvents:"none"}} />
            </div>
          : <div style={{width:"100%",height:120,background:"#0e0e0e",display:"flex",alignItems:"center",justifyContent:"center",fontSize:56,borderBottom:"1px solid #1a1a1a"}}>{mIcon(m.type)}</div>}

        {/* Info panel */}
        <div style={{padding:"10px 12px 12px"}}>

          {/* Icon + name/subtitle row */}
          <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
            {m.photos?.[0]&&<span style={{fontSize:24,flexShrink:0,marginTop:2,lineHeight:1}}>{mIcon(m.type)}</span>}
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:6}}>
                <div className={timerRunning?"loading-rat":undefined} style={{flex:1,minWidth:0,fontSize:15,fontWeight:700,color:TXT,lineHeight:1.25}}>
                  {hl(m.name,searchTokens)}
                  {timerRunning&&<span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:GRN,boxShadow:"0 0 6px "+GRN,marginLeft:7,verticalAlign:"middle"}}/>}
                </div>
                {!hideCollapse&&<span style={{fontSize:10,color:"#555",flexShrink:0,marginTop:2,userSelect:"none"}}>{open?"▲":"▼"}</span>}
              </div>
              {[m.make,m.model,m.year,m.source].filter(Boolean).length>0&&
                <div style={{fontSize:11,color:MUT,marginTop:3,lineHeight:1.4}}>
                  {hl([m.make,m.model,m.year].filter(Boolean).join(" · "),searchTokens)}
                  {m.source&&<span style={{color:"#444"}}> · {hl(m.source,searchTokens)}</span>}
                </div>}
              {m.type&&!typeBadgeActive&&<div style={{fontSize:9,color:"#555",marginTop:2,letterSpacing:"0.06em",textTransform:"uppercase"}}>{hl(m.type,searchTokens)}</div>}
            </div>
          </div>

          {/* Badges — full width below info */}
          <div style={{display:"flex",alignItems:"center",gap:4,marginTop:9,flexWrap:"wrap"}}>
            {(m.tileFields&&m.tileFields.length>0?m.tileFields:DEFAULT_TILE).map(k=>{
              const tc=m.tileColors||{};
              const colIdx=tc[k]!==undefined?tc[k]:(TILE_COLOR_DEFAULTS[k]!==undefined&&TILE_COLOR_DEFAULTS[k]!=="auto"?TILE_COLOR_DEFAULTS[k]:0);
              const [cbg,cbrd,ctxt]=BADGE_PALETTE[colIdx]||BADGE_PALETTE[0];
              const bStyle={fontSize:9,fontWeight:700,letterSpacing:"0.08em",padding:"3px 8px",borderRadius:3,fontFamily:"'IBM Plex Mono',monospace",background:cbg,color:ctxt,border:"1px solid "+cbrd,whiteSpace:"nowrap"};
              if(k==="strokeType"&&m.strokeType) return <span key="st" style={{fontSize:9,fontWeight:700,letterSpacing:"0.08em",padding:"3px 8px",borderRadius:3,fontFamily:"'IBM Plex Mono',monospace",background:m.strokeType==="4-stroke"?"#0e1a2a":m.strokeType==="Diesel"?"#0e200e":ACC+"22",color:m.strokeType==="4-stroke"?"#3a7bd5":m.strokeType==="Diesel"?"#3d9e50":ACC,border:"1px solid "+(m.strokeType==="4-stroke"?"#3a7bd555":m.strokeType==="Diesel"?"#3d9e5055":ACC+"55"),whiteSpace:"nowrap"}}>{m.strokeType==="4-stroke"?"4T":m.strokeType==="Diesel"?"DSL":"2T"}</span>;
              if(k==="rage"&&(m.rage||0)>0) return <span key="rage" style={{fontSize:10,letterSpacing:-2}}>{"☠️".repeat(m.rage)}</span>;
              const field=ALL_BADGE_FIELDS.find(f=>f.k===k);
              if(field&&m[k]){const lbl=(field.s?field.s.replace(":",""):field.l.split("/")[0].trim().split(" ").slice(0,2).join(" "));return <span key={k} style={bStyle}>{lbl}: {String(m[k]).slice(0,14)}</span>;}
              // Not in the curated list — the Tile picker (config.jsx's
              // TileConfig) also offers every getMachineSpecEntries field,
              // keyed by its label rather than a raw machine key, since many
              // combine several fields into one value (e.g. "Front
              // Suspension") or come from a nested object like carbSpec that
              // a raw key can't address at all. Render using that entry's
              // already-formatted value.
              const specEntry=specs.find(s=>s.label===k);
              if(specEntry){const lbl=k.split(" ").slice(0,2).join(" ");return <span key={k} style={bStyle}>{lbl}: {String(specEntry.value).slice(0,14)}</span>;}
              // Last-resort fallback for a raw key with data that's in
              // neither list (shouldn't normally happen now that
              // getMachineSpecEntries covers essentially every field).
              if(!field&&m[k]&&typeof m[k]!=="object"){const lbl=humanizeKey(k).split(" ").slice(0,2).join(" ");return <span key={k} style={bStyle}>{lbl}: {String(m[k]).slice(0,14)}</span>;}
              return null;
            })}
            {m.complete&&<span style={{fontSize:9,fontWeight:700,letterSpacing:"0.08em",padding:"3px 8px",borderRadius:3,background:GRN+"22",color:GRN,border:"1px solid "+GRN+"44",whiteSpace:"nowrap"}}>✓ READY FOR PICKUP</span>}
            {svcStatus.overdue&&<span style={{fontSize:9,fontWeight:700,letterSpacing:"0.08em",padding:"3px 8px",borderRadius:3,background:RED+"22",color:RED,border:"1px solid "+RED+"44",whiteSpace:"nowrap"}}>SERVICE</span>}
            {!svcStatus.overdue&&svcStatus.dueSoon&&<span style={{fontSize:9,fontWeight:700,letterSpacing:"0.08em",padding:"3px 8px",borderRadius:3,background:"#e8870a22",color:"#e8870a",border:"1px solid #e8870a44",whiteSpace:"nowrap"}}>DUE SOON</span>}
          </div>

          {/* Client / due / stats */}
          {(clientName||m.dueDate||(m.timeLog||[]).reduce((s,e)=>s+(e.seconds||0),0)>0||(m.rage||0)>0)&&
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6,flexWrap:"wrap"}}>
              {clientName&&<span style={{fontSize:9,color:ACC,whiteSpace:"nowrap"}}>👤 {clientName}</span>}
              {m.dueDate&&(()=>{const due=new Date(m.dueDate);const now=new Date();const overdue=due<now;const dueColor=overdue?"#e87a0a":now.toDateString()===due.toDateString()?"#4a9eff":MUT;return<span style={{fontSize:9,color:dueColor,whiteSpace:"nowrap"}}>{overdue?"⚠ OVERDUE":"DUE "}{!overdue&&due.toLocaleDateString('en-AU',{day:'numeric',month:'short'})}</span>;})()}
              {(()=>{const tHrs=(m.timeLog||[]).reduce((s,e)=>s+(e.seconds||0),0)/3600;const hasHrs=tHrs>0;const hasRage=(m.rage||0)>0;if(!hasHrs&&!hasRage)return null;return<>{hasHrs&&<span style={{fontSize:9,color:GRN,fontFamily:"'IBM Plex Mono',monospace"}}>{tHrs.toFixed(1)}h</span>}{hasRage&&<span style={{fontSize:9,color:RED,letterSpacing:-1}}>{"☠️".repeat(m.rage)}</span>}</>;})()}
            </div>}

          {specMatch&&(
            <div style={{fontSize:9,color:MUT,marginTop:6,lineHeight:1.4}}>
              <span style={{color:ACC,textTransform:"uppercase",letterSpacing:"0.06em",fontSize:8}}>{specMatch.label}:</span>{" "}
              {hl(specMatch.value,searchTokens)}
            </div>
          )}

        </div>
      </div>

      {open&&(
        <div className="card-expand" style={{borderTop:"1px solid "+BRD2}}>
          {(()=>{
            const ef = m.expandFields&&m.expandFields.length>0 ? m.expandFields : DEFAULT_EXPAND;
            const show = k => ef.includes(k);
            const hiddenSpecFields = new Set(m.hiddenSpecFields||[]);
            // Every scalar spec field is individually toggled via
            // hiddenSpecFields now (see ExpandConfig) — no coarse bucket
            // layer, so a field's visibility can never drift out of sync
            // with what the picker actually offers.
            const visibleSpecs = specs.filter(s=>!hiddenSpecFields.has(s.label));
            return <>
              {show("photos")&&m.photos?.length>0&&<div style={{padding:"10px 14px 0"}}><div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><FL t="Photos" /></div><div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:5,marginTop:4}}>{m.photos.map((p,i)=><div key={i} style={{position:"relative"}}><img src={p} alt="" onClick={()=>setFullImg({src:p,idx:i})} style={{width:"100%",height:80,objectFit:"cover",borderRadius:"2px 2px 0 0",border:i===0?"1px solid "+ACC+"88":"1px solid "+BRD,borderBottom:"none",cursor:"zoom-in",display:"block"}} /><button title="Delete photo" onClick={ev=>{ev.stopPropagation();deletePhotoAt(i);}} style={{position:"absolute",top:4,right:4,background:"rgba(0,0,0,0.75)",border:"1px solid "+RED+"88",color:"#fff",width:20,height:20,borderRadius:"50%",cursor:"pointer",fontSize:10,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>✕</button><button title={i===0?"Cover photo":"Set as cover"} className={"cover-bar-tactile"+(i===0?" on":"")} onClick={ev=>{ev.stopPropagation();if(i===0)return;const r=[p,...m.photos.filter((_,j)=>j!==i)];onUpdate({...m,photos:r});}} style={{width:"100%",minHeight:34,background:i===0?"#120c06":"#1a1a1a",border:"1px solid "+(i===0?ACC+"66":BRD),borderTop:"none",borderRadius:"0 0 2px 2px",cursor:i===0?"default":"pointer",fontSize:9,fontWeight:700,color:i===0?ACC:MUT,fontFamily:"'IBM Plex Mono',monospace",padding:4}}>{i===0?"★ Cover":"☆ Set as Cover"}</button></div>)}</div></div>}
              {show("desc")&&m.desc&&<div style={{padding:"10px 14px 0"}}><div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><FL t="Description" /></div><div style={{fontSize:11,color:"#999",lineHeight:1.5,marginTop:2}}>{m.desc}</div></div>}
              {visibleSpecs.length>0&&<div style={{padding:"12px 14px 0"}}><div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Engine Spec" /></div><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5}}>{visibleSpecs.map(s=><SpecCell key={s.label} label={s.label} value={s.value} highlight={s.highlight} />)}</div></div>}
              {show("fasteners")&&m.fasteners&&m.fasteners.length>0&&<div style={{padding:"12px 14px 0"}}>
                <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Fastener Specs" /></div>
                <div style={{marginTop:6}}>
                  {m.fasteners.map((f,idx)=>{
                    const loc=f.location==="Other"?(f.locOther||"Other"):(f.location||"—");
                    const parts=[
                      f.fType,
                      f.fType==="Bolt"&&f.driveType?f.driveType:null,
                      f.diameter?f.diameter+" dia":null,
                      f.length?f.length+"mm length":null,
                      f.spacing?f.spacing+"mm ctr spacing":null,
                      f.countPerSide?f.countPerSide+"/side":null,
                      f.torqueNm?f.torqueNm+"Nm torque":null,
                    ].filter(Boolean);
                    return <div key={f.id||idx} style={{background:"#0d0d0d",border:"1px solid #252525",borderRadius:2,padding:"8px 10px",marginBottom:5}}>
                      <div style={{fontSize:8,color:ACC,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:3}}>{loc}</div>
                      <div style={{fontSize:11,color:TXT,fontFamily:"'IBM Plex Mono',monospace",lineHeight:1.6}}>{parts.length?parts.join(" · "):"No specs"}</div>
                    </div>;
                  })}
                </div>
              </div>}
              {show("lighting")&&m.lighting?.length>0&&<div style={{padding:"12px 14px 0"}}>
                <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Lighting" /></div>
                <div style={{marginTop:6}}>
                  {m.lighting.map((l,idx)=>{
                    const loc=l.location==="Other"?(l.locationOther||"Other"):(l.location||"—");
                    const parts=[l.lightType,l.wattage?l.wattage+"W":null,l.voltage,l.amperage?l.amperage+"A":null,l.plug].filter(Boolean);
                    return <div key={l.id||idx} style={{background:"#0d0d0d",border:"1px solid #252525",borderRadius:2,padding:"8px 10px",marginBottom:5}}>
                      <div style={{fontSize:8,color:ACC,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:3}}>{loc}</div>
                      <div style={{fontSize:11,color:TXT,fontFamily:"'IBM Plex Mono',monospace",lineHeight:1.6}}>{parts.length?parts.join(" · "):"No specs"}</div>
                      {l.notes&&<div style={{fontSize:10,color:MUT,marginTop:2}}>{l.notes}</div>}
                    </div>;
                  })}
                </div>
              </div>}
              {show("bearings")&&m.bearings?.length>0&&<div style={{padding:"12px 14px 0"}}>
                <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Bearings" /></div>
                <div style={{marginTop:6}}>
                  {m.bearings.map((b,idx)=>{
                    const loc=b.location==="Other"?(b.locationOther||"Other"):(b.location||"—");
                    const parts=[b.type,b.partNo,b.clearance?b.clearance+"mm clearance":null,b.preload?b.preload+"Nm preload":null].filter(Boolean);
                    return <div key={b.id||idx} style={{background:"#0d0d0d",border:"1px solid #252525",borderRadius:2,padding:"8px 10px",marginBottom:5}}>
                      <div style={{fontSize:8,color:ACC,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:3}}>{loc}</div>
                      <div style={{fontSize:11,color:TXT,fontFamily:"'IBM Plex Mono',monospace",lineHeight:1.6}}>{parts.length?parts.join(" · "):"No specs"}</div>
                      {b.notes&&<div style={{fontSize:10,color:MUT,marginTop:2}}>{b.notes}</div>}
                    </div>;
                  })}
                </div>
              </div>}
              {show("belts")&&m.belts?.length>0&&<div style={{padding:"12px 14px 0"}}>
                <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Belts" /></div>
                <div style={{marginTop:6}}>
                  {m.belts.map((b,idx)=>{
                    const parts=[b.beltType,b.beltCount?b.beltCount+" belt"+(b.beltCount!=="1"?"s":""):null,b.beltPartNo||null,b.beltWidth&&b.beltLength?b.beltWidth+"×"+b.beltLength+"mm":b.beltWidth?b.beltWidth+"mm wide":b.beltLength?b.beltLength+"mm long":null].filter(Boolean);
                    return <div key={b.id||idx} style={{background:"#0d0d0d",border:"1px solid #252525",borderRadius:2,padding:"8px 10px",marginBottom:5}}>
                      {b.beltFunction&&<div style={{fontSize:8,color:ACC,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:3}}>{b.beltFunction}</div>}
                      <div style={{fontSize:11,color:TXT,fontFamily:"'IBM Plex Mono',monospace",lineHeight:1.6}}>{parts.length?parts.join(" · "):"No specs"}</div>
                      {b.beltNotes&&<div style={{fontSize:10,color:MUT,marginTop:2}}>{b.beltNotes}</div>}
                    </div>;
                  })}
                </div>
              </div>}
              {show("batteries")&&m.batteries?.length>0&&<div style={{padding:"12px 14px 0"}}>
                <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Batteries" /></div>
                <div style={{marginTop:6}}>
                  {m.batteries.map((b,idx)=>{
                    const parts=[b.battType,b.voltage,b.cca?b.cca+" CCA":null,b.ah?b.ah+" Ah":null,b.dimensions].filter(Boolean);
                    return <div key={b.id||idx} style={{background:"#0d0d0d",border:"1px solid #252525",borderRadius:2,padding:"8px 10px",marginBottom:5}}>
                      {b.label&&<div style={{fontSize:8,color:ACC,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:3}}>{b.label}</div>}
                      <div style={{fontSize:11,color:TXT,fontFamily:"'IBM Plex Mono',monospace",lineHeight:1.6}}>{parts.length?parts.join(" · "):"No specs"}</div>
                    </div>;
                  })}
                </div>
              </div>}
              {show("fuseBoxes")&&m.fuseBoxes?.length>0&&<div style={{padding:"12px 14px 0"}}>
                <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Fuse Boxes" /></div>
                <div style={{marginTop:6}}>
                  {m.fuseBoxes.map((box,idx)=>(
                    <div key={box.id||idx} style={{background:"#0d0d0d",border:"1px solid #252525",borderRadius:2,padding:"8px 10px",marginBottom:5}}>
                      <div style={{fontSize:8,color:ACC,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:3}}>{box.label||"Fuse Box"}{box.location?" · "+box.location:""}</div>
                      <div style={{fontSize:11,color:TXT,fontFamily:"'IBM Plex Mono',monospace",lineHeight:1.6}}>{(box.fuses||[]).length?box.fuses.map((f,i)=>(f.amperage||"?")+"A"+(f.circuit?" "+f.circuit:"")).join(" · "):"No fuses logged"}</div>
                    </div>
                  ))}
                </div>
              </div>}
              {show("hydRams")&&m.hydRams?.length>0&&<div style={{padding:"12px 14px 0"}}>
                <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Hydraulic Rams" /></div>
                <div style={{marginTop:6}}>
                  {m.hydRams.map((r,idx)=>{
                    const loc=r.location==="Other"?(r.locationOther||"Other"):(r.location||"—");
                    const parts=[r.bore?r.bore+"mm bore":null,r.rod?r.rod+"mm rod":null,r.stroke?r.stroke+"mm stroke":null,r.collapsed?r.collapsed+"mm collapsed":null,r.sealKit?"Seal: "+r.sealKit:null].filter(Boolean);
                    return <div key={r.id||idx} style={{background:"#0d0d0d",border:"1px solid #252525",borderRadius:2,padding:"8px 10px",marginBottom:5}}>
                      <div style={{fontSize:8,color:ACC,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:3}}>{loc}</div>
                      <div style={{fontSize:11,color:TXT,fontFamily:"'IBM Plex Mono',monospace",lineHeight:1.6}}>{parts.length?parts.join(" · "):"No specs"}</div>
                      {r.notes&&<div style={{fontSize:10,color:MUT,marginTop:2}}>{r.notes}</div>}
                    </div>;
                  })}
                </div>
              </div>}
              {show("attachments")&&m.attachments?.length>0&&<div style={{padding:"12px 14px 0"}}>
                <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Attachments" /></div>
                <div style={{marginTop:6}}>
                  {m.attachments.map((a,idx)=>{
                    const parts=[a.attachType==="Other"?a.attachTypeOther:a.attachType,a.sizeSpec,a.weight?a.weight+"kg":null].filter(Boolean);
                    return <div key={a.id||idx} style={{background:"#0d0d0d",border:"1px solid #252525",borderRadius:2,padding:"8px 10px",marginBottom:5}}>
                      <div style={{fontSize:11,color:TXT,fontFamily:"'IBM Plex Mono',monospace",lineHeight:1.6}}>{parts.length?parts.join(" · "):"No specs"}</div>
                      {a.notes&&<div style={{fontSize:10,color:MUT,marginTop:2}}>{a.notes}</div>}
                    </div>;
                  })}
                </div>
              </div>}
              {show("notes")&&m.notes&&<div style={{padding:"10px 14px 0"}}><div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><FL t="Notes" /></div><div style={{fontSize:11,color:"#999",lineHeight:1.5,marginTop:2}}>{m.notes}</div></div>}
              {show("parts")&&m.parts?.length>0&&(
                <div style={{padding:"12px 14px 0"}}>
                  <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Parts Used" /></div>
                  <div style={{marginTop:6}}>
                    {m.parts.map((p,idx)=>{
                      const qty=Number(p.qty)||1;
                      const buy=(parseFloat(p.buyPrice)||0)*qty;
                      const sell=(parseFloat(p.sellPrice)||0)*qty;
                      return <div key={p.id||idx} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid #181818"}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:10,color:TXT,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                          <div style={{fontSize:8,color:MUT}}>{[p.partNumber,p.brand,`Qty ${qty}`].filter(Boolean).join(" · ")}</div>
                        </div>
                        <div style={{display:"flex",gap:6,flexShrink:0,marginLeft:8,alignItems:"center"}}>
                          {buy>0&&<span style={{fontSize:9,color:MUT}}>${buy.toFixed(2)}</span>}
                          {sell>0&&<span style={{fontSize:9,color:GRN}}>${sell.toFixed(2)}</span>}
                        </div>
                      </div>;
                    })}
                    <div style={{fontSize:9,color:GRN,textAlign:"right",marginTop:5,fontWeight:700}}>
                      ${m.parts.reduce((s,p)=>(s+(parseFloat(p.sellPrice)||0)*(Number(p.qty)||1)),0).toFixed(2)} total parts
                    </div>
                  </div>
                </div>
              )}
              <div style={{height:1,background:BRD2,margin:"12px 0 0"}} />
            </>;
          })()}

          {(()=>{const ef=m.expandFields&&m.expandFields.length>0?m.expandFields:DEFAULT_EXPAND;const showSvcH=ef.includes("serviceHistory");return showSvcH&&(
          <div style={{padding:"12px 14px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8}}><SL t="Service History" /></div>
              <button style={{...btnA,...sm}} onClick={ev=>{ev.stopPropagation();setShowSvc(true);}}>+ Log</button>
            </div>
            {saving&&<div style={{fontSize:10,color:MUT,marginBottom:8}}>Saving...</div>}
            {!loaded&&<div style={{fontSize:10,color:MUT}}>Loading...</div>}
            {loaded&&svcs.length===0&&<Empty t="No entries yet" />}
            {loaded&&svcs.length>0&&(
              <div style={{borderLeft:"2px solid "+ACC+"33",paddingLeft:10,marginLeft:6}}>
                {svcs.map(svc=>(
                  <div key={svc.id} style={{position:"relative",paddingLeft:18,marginBottom:14}}>
                    <div style={{position:"absolute",left:-13,top:5,width:6,height:6,borderRadius:"50%",background:ACC,flexShrink:0}} />
                    <div style={{fontSize:9,color:MUT,marginBottom:2}}>{fmtDT(svc.completedAt)}</div>
                    <div style={{fontSize:13,fontWeight:700,color:TXT,marginBottom:3}}>{svc.types.join("  ·  ")}</div>
                    {svc.notes&&<div style={{fontSize:11,color:"#888",lineHeight:1.5,marginBottom:5}}>{svc.notes}</div>}
                    {svc.plugPhoto&&<div style={{marginBottom:6}}><FL t="Spark Plug" /><img src={svc.plugPhoto} alt="" onClick={()=>setFullImg(svc.plugPhoto)} style={{borderRadius:2,maxWidth:"100%",maxHeight:130,objectFit:"cover",border:"1px solid "+BRD,cursor:"zoom-in",display:"block"}} /></div>}
                    {svc.jobPhotos?.length>0&&<div style={{marginBottom:6}}><FL t={"Job Photos ("+svc.jobPhotos.length+")"} /><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>{svc.jobPhotos.map((p,i)=><img key={i} src={p} alt="" onClick={()=>setFullImg(p)} style={{width:"100%",height:70,objectFit:"cover",borderRadius:2,border:"1px solid "+BRD,cursor:"zoom-in",display:"block"}} />)}</div></div>}
                    <div style={{display:"flex",gap:6,marginTop:5}}>
                      <button style={{...btnG,...sm}} onClick={()=>setEditSvc(svc)}>Edit</button>
                      <button style={btnD} onClick={()=>delSvc(svc.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          );})()}
          {onMoveToBench&&(
            <div style={{padding:"0 14px 12px"}}>
              <button style={{width:"100%",background:"none",border:"1px solid "+ACC+"55",borderRadius:3,padding:"14px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:ACC,fontSize:12}} onClick={ev=>{ev.stopPropagation();onMoveToBench(m);}}>
                <span style={{fontSize:20}}>🔧</span> Move to Bench
              </button>
            </div>
          )}
          {storagePolicyEnabled&&(
            <div style={{padding:"0 14px 12px"}}>
              <div style={{borderLeft:"2px solid "+ACC,paddingLeft:8,marginBottom:10}}><SL t="Storage" /></div>
              <MoveToStoragePanel machine={m} profile={profile} onUpdate={onUpdate} onBooked={onBookIn} />
            </div>
          )}
          {showPdfOpts&&<Suspense fallback={null}><PdfExportModal m={m} svcs={svcs} onClose={()=>setShowPdfOpts(false)}/></Suspense>}
          <div style={{padding:"0 10px 14px",display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
            {withGuide("all specs\n& intervals",<button style={_jEdit} onClick={ev=>{ev.stopPropagation();setShowEdit(true);}}>Edit Machine</button>)}
            {withGuide("export\nspec sheet",<button style={_jPdf} onClick={ev=>{ev.stopPropagation();if(!loaded){getServices(m.id).then(s=>{setSvcs(s||[]);setLoaded(true);setShowPdfOpts(true);});}else setShowPdfOpts(true);}}>📄 PDF</button>)}
            {wikiFlagOn&&!isGuest&&m.make&&m.model&&<button style={_jWiki} onClick={ev=>{ev.stopPropagation();setShowWiki(true);}}>🌐 Wiki</button>}
            {withGuide("public\nlink ↗",<button style={_jShare} onClick={ev=>{ev.stopPropagation();navigator.clipboard.writeText(window.location.origin+'/m/'+m.id);setCopied(true);setTimeout(()=>setCopied(false),2000);}}>{copied?'✓ Copied':'🔗 Share'}</button>)}
            {withGuide("customise\nlayout",<button style={_jLayout} onClick={ev=>{ev.stopPropagation();setShowExpandConfig(true);}}>⚙️ Layout</button>)}
            {withGuide("configure\nbadges",<button style={_jTile} onClick={ev=>{ev.stopPropagation();setShowConfig(true);}}>⚙️ Tile</button>)}
            {onClose&&<button style={{..._jClose,gridColumn:"1/-1"}} onClick={ev=>{ev.stopPropagation();onClose();}}>✕ Close</button>}
            <button style={{..._jDel,gridColumn:"1/-1"}} onClick={ev=>{ev.stopPropagation();onDelete(m);}}>Delete</button>
          </div>
          {showGuide&&(
            <div style={{padding:"0 14px 14px",textAlign:"right"}}>
              <button onClick={ev=>{ev.stopPropagation();onTutDismiss?.();}} style={{background:"none",border:"none",color:"#333",fontSize:8,cursor:"pointer",fontFamily:"'IBM Plex Mono',monospace",padding:0,letterSpacing:"0.05em"}}>got it</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
export default MachineCard;